/** @jsx jsx */
import { jsx } from '@emotion/core';
import React from 'react';
import PropTypes from 'prop-types';
import { withNamespaces } from 'react-i18next';
import get from 'lodash/get';
import defaultsDeep from 'lodash/defaultsDeep';
import Modal from '../../../../ui/Modal';
import SolutionsList from './SolutionsList';
import DropdownButton from './DropdownButton';
import { Breakpoints } from '../../../../../styles/Grid/GridSizes';
import { MediaQueries } from '../../../../../styles/Grid';
import { RICH_FILTERING as dict } from '../../../../../utils/dictionarykeys';
import { SafePureComponent } from '../../../../../utils/SafePureComponent';
import { WORSKSPACE_PARAMETER } from '../../../../../utils/constants';
import {
  showElementCss,
  hideElementCss,
} from '../../../../../utils/responsive';
import { getDecodedQueryParams } from '../../../../../utils/url';
import * as styles from './index.styles';

const defaultData = [
  {
    Label: { value: 'Office Space' },
    RawValue: { value: '/office-space' },
  },
  {
    Label: { value: 'Coworking' },
    RawValue: { value: '/coworking' },
  },
  {
    Label: { value: 'Virtual Office' },
    RawValue: { value: '/virtual-offices' },
  },
  {
    Label: { value: 'Meeting Rooms' },
    RawValue: { value: '/meeting-rooms' },
  },
];

/**
 * Get the solution within solutions if found in the URL
 * @param  {Array} solutions
 * @return {Object} The solution that matches URL parameter
 */
const getCurrentSolution = solutions =>
  solutions.find(solution => {
    const queryString = get(solution, 'RawValue.value');
    const params = getDecodedQueryParams();
    const wsValue = params[WORSKSPACE_PARAMETER];

    return queryString === `/${wsValue}`;
  });

export class SolutionsDropdown extends SafePureComponent {
  constructor(props) {
    super(props);
    this.fields = defaultsDeep(this.props.fields.items, defaultData);
    this.state = {
      isOpen: false,
    };
  }

  solutionListRef = React.createRef();
  dropdownButtonRef = React.createRef();

  componentDidUpdate(_prevProps, prevState) {
    const { isOpen } = this.state;
    const openChanged = prevState.isOpen !== isOpen;
    if (openChanged && isOpen) {
      window.addEventListener('click', this.onClickOutsideModal);
    } else if (openChanged && !isOpen) {
      window.removeEventListener('click', this.onClickOutsideModal);
    }
  }

  onClickOutsideModal = event => {
    event.stopPropagation();
    const { target } = event;
    const { current: ancestor } = this.solutionListRef;
    const isTriggerButton = this.dropdownButtonRef.current === target;
    const isClickingOutside = !ancestor.contains(target);

    if (isClickingOutside && !isTriggerButton) {
      this.closeModal();
    }
  };

  closeModal = () => {
    this.setState({
      isOpen: false,
    });
  };

  toggleModal = () => {
    this.setState({
      isOpen: !this.state.isOpen,
    });
  };

  safeRender() {
    const { t } = this.props;
    const fields = this.fields;
    const currentSolution = getCurrentSolution(fields);
    const currentSolutionLabel = get(
      currentSolution,
      'Label.value',
      get(fields, '[0].Label.value'),
    );
    const solutionQueryString = get(
      currentSolution,
      'RawValue.value',
      '/office-space',
    );

    const { state, closeModal, toggleModal } = this;
    const { isOpen } = state;
    const modalHeadline = t(dict.FIND_YOUR_SOLUTION);
    const shouldAllowScrollAfterOpen = window.innerWidth >= Breakpoints.lg;

    return (
      <div css={styles.dropdownSolutions}>
        <DropdownButton
          buttonLabel={currentSolutionLabel}
          toggleModal={toggleModal}
          innerRef={this.dropdownButtonRef}
        />
        <div css={isOpen && styles.modalWrapper} id="solution-dropdown">
          <Modal
            allowScrollAfterOpen={shouldAllowScrollAfterOpen}
            customCloseButtonCss={hideElementCss(MediaQueries.lgAndUp)}
            isOpen={isOpen}
            onRequestClose={closeModal}
            overlayProp={styles.modalCustomCss}
            parentSelector={() => document.querySelector('#solution-dropdown')}
          >
            <SolutionsList
              currentQueryString={solutionQueryString}
              fields={fields}
              modalHeadline={modalHeadline}
              innerRef={this.solutionListRef}
              closeModal={this.closeModal}
            >
              <DropdownButton
                buttonLabel={currentSolutionLabel}
                customCss={[
                  styles.dropdownButton,
                  showElementCss(MediaQueries.lgAndUp),
                ]}
                toggleModal={toggleModal}
              />
            </SolutionsList>
          </Modal>
        </div>
      </div>
    );
  }
}

SolutionsDropdown.defaultProps = {
  fields: defaultData,
  t: key => key,
};

SolutionsDropdown.propTypes = {
  fields: PropTypes.shape({
    items: PropTypes.arrayOf(
      PropTypes.shape({
        fields: PropTypes.shape({
          Label: PropTypes.shape({ value: PropTypes.string }),
          RawValue: PropTypes.shape({ value: PropTypes.string }),
        }),
      }),
    ),
  }),
};

SolutionsDropdown.displayName = 'SolutionsDropdown';

export default withNamespaces()(SolutionsDropdown);
